import { useCallback, useEffect, useRef, useState } from 'react';
import type { EquipmentFileDto, ProcedureItem, ProductDto, ProductImportResult, ProductListDto } from '@app/shared';
import { api } from '../api';
import { platform } from '../platform';
import { useApp } from '../state';
import { Icon, plural, Sheet, when } from '../ui';
import { SubHead } from './admin/SubHead';
import { ItemsEditor, StepContent, StepList } from './Steps';

const num = (n: number) => n.toLocaleString('ru-RU', { maximumFractionDigits: 3 });

/** Номенклатура: поиск, загрузка из Excel/1С (администратор). */
export function Products() {
  const { fail, open, can, toast } = useApp();
  const [list, setList] = useState<ProductListDto[] | null>(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ProductImportResult | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setList(await api.products(q));
    } catch (e) {
      fail(e);
    }
  }, [q, fail]);
  useEffect(() => {
    const t = setTimeout(() => void load(), 250);
    return () => clearTimeout(t);
  }, [load]);

  async function upload(f: File | undefined) {
    if (input.current) input.current.value = '';
    if (!f) return;
    setBusy(true);
    try {
      const r = await api.importProducts(f);
      setResult(r);
      platform.haptic('success');
      toast(`Загружено: ${r.products} ${plural(r.products, 'позиция', 'позиции', 'позиций')}`);
      await load();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <SubHead title="Номенклатура">
        {can('equipment.manage') && (
          <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => input.current?.click()}>
            <Icon name="upload" size={16} />
            {busy ? 'Загружаю…' : 'Из Excel'}
          </button>
        )}
      </SubHead>
      <input ref={input} type="file" accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={(e) => void upload(e.target.files?.[0])} />
      {can('equipment.manage') && (
        <details className="card flat small">
          <summary style={{ cursor: 'pointer' }}>Какой файл подходит</summary>
          <p style={{ margin: '8px 0 0' }}>
            Выгрузка из 1С или Excel (.xlsx или .csv). Первая строка — заголовки: «Артикул» (или «Код»), «Наименование», «Ячейка» (или «Место хранения»), «Количество» (или
            «Остаток»). Остальные столбцы станут характеристиками. Одна строка — одно место хранения; артикул может повторяться. Повторная загрузка обновляет данные и остатки.
          </p>
        </details>
      )}
      {result && (
        <div className="card flat small" role="status">
          Новых {result.created}, обновлено {result.updated}, мест хранения {result.places}
          {result.skipped ? `, пропущено строк без артикула: ${result.skipped}` : ''}.
          <br />
          Столбцы: артикул — «{result.columns.code}», наименование — «{result.columns.name}»
          {result.columns.place ? `, место — «${result.columns.place}»` : ', мест хранения нет'}
          {result.columns.qty ? `, количество — «${result.columns.qty}»` : ''}
          {result.columns.specs.length ? `; характеристики: ${result.columns.specs.join(', ')}` : ''}.
        </div>
      )}
      <div className="search">
        <Icon name="search" size={18} />
        <input className="input" type="search" placeholder="Артикул или наименование" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Поиск номенклатуры" />
      </div>
      {!list && <div className="skeleton" />}
      {list && list.length === 0 && <p className="muted">{q ? 'Ничего не нашлось.' : 'Номенклатуры пока нет — загрузите выгрузку из Excel или 1С.'}</p>}
      <div className="list">
        {list?.map((p) => (
          <button key={p.id} type="button" className="item" onClick={() => open({ name: 'product', id: p.id })}>
            <span className="grow stack" style={{ gap: 2 }}>
              <span className="title ellipsis">{p.code}</span>
              <span className="sub ellipsis">{p.name}</span>
            </span>
            <span className="small muted" style={{ textAlign: 'right' }}>
              {num(p.total)} шт.
              <br />
              {p.places} {plural(p.places, 'место', 'места', 'мест')}
            </span>
            <span className="chev">
              <Icon name="right" size={18} />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Характеристики и где лежит: всего, сколько мест, по местам. Используется в карточке и в задаче. */
export function ProductFacts({ p, qty }: { p: ProductDto; qty?: number | null }) {
  return (
    <>
      <div className="row wrap" style={{ gap: 8 }}>
        {qty !== undefined && qty !== null && <span className="badge solid">Задание: {num(qty)} шт.</span>}
        <span className="badge outline">
          На складе: {num(p.total)} шт. · {p.places} {plural(p.places, 'место', 'места', 'мест')}
        </span>
      </div>
      {p.specs.length > 0 && (
        <section className="stack" style={{ gap: 6 }}>
          <h2 className="section-title">Характеристики</h2>
          <dl className="facts">
            {p.specs.map((s) => (
              <div key={s.k}>
                <dt>{s.k}</dt>
                <dd>{s.v}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
      <section className="stack" style={{ gap: 6 }}>
        <h2 className="section-title">
          Места хранения · {p.places}
          {p.stockUpdatedAt ? ` · на ${when(p.stockUpdatedAt)}` : ''}
        </h2>
        {p.stock.length === 0 ? (
          <p className="small muted" style={{ margin: 0 }}>
            Остатков нет.
          </p>
        ) : (
          <dl className="facts">
            {p.stock.map((s) => (
              <div key={s.place}>
                <dt>{s.place}</dt>
                <dd>{num(s.qty)} шт.</dd>
              </div>
            ))}
          </dl>
        )}
      </section>
    </>
  );
}

/** Карточка номенклатуры: характеристики, места хранения, переналадка на неё. */
export function Product({ id }: { id: string }) {
  const { fail, can, toast } = useApp();
  const [p, setP] = useState<ProductDto | null>(null);
  const [edit, setEdit] = useState(false);
  const [step, setStep] = useState<{ item: ProcedureItem; n: number } | null>(null);
  const manage = can('equipment.manage');

  const load = useCallback(async () => {
    try {
      setP(await api.product(id));
    } catch (e) {
      fail(e);
    }
  }, [id, fail]);
  useEffect(() => void load(), [load]);

  if (!p) {
    return (
      <div className="stack">
        <SubHead title="Номенклатура" />
        <div className="skeleton" />
      </div>
    );
  }
  return (
    <div className="stack">
      <SubHead title={p.code} />
      <p style={{ margin: '-6px 0 0' }}>{p.name}</p>
      <ProductFacts p={p} />
      <section className="stack" style={{ gap: 8 }}>
        <div className="proc-head">
          <h2 className="section-title">Переналадка на {p.code}</h2>
          {manage && (
            <button type="button" className="icon-btn" aria-label="Изменить переналадку" onClick={() => setEdit(true)}>
              <Icon name="edit" size={16} />
            </button>
          )}
        </div>
        {p.changeover.length ? (
          <StepList items={p.changeover} manage={false} onOpen={(item, n) => setStep({ item, n })} />
        ) : (
          <p className="small muted" style={{ margin: 0 }}>
            {manage ? 'Что меняем и на что — добавьте пункты (карандаш). Одинаковые действия берите из библиотеки — они подключатся ко всем номенклатурам сразу.' : 'Список переналадки не заполнен.'}
          </p>
        )}
      </section>
      {step && (
        <Sheet title={`Переналадка · ${step.n + 1}`} onClose={() => setStep(null)}>
          <p className="step-title">{step.item.text}</p>
          <StepContent equipmentId="" details={step.item.details} fileIds={step.item.fileIds} files={p.files as EquipmentFileDto[]} />
        </Sheet>
      )}
      {edit && (
        <ChangeoverEditor
          p={p}
          onClose={() => setEdit(false)}
          onSaved={(next) => {
            setP(next);
            setEdit(false);
            toast('Переналадка сохранена');
          }}
        />
      )}
    </div>
  );
}

function ChangeoverEditor({ p, onClose, onSaved }: { p: ProductDto; onClose: () => void; onSaved: (p: ProductDto) => void }) {
  const { fail } = useApp();
  const [items, setItems] = useState<ProcedureItem[]>(() => structuredClone(p.changeover));
  const [files, setFiles] = useState<EquipmentFileDto[]>(p.files as EquipmentFileDto[]);
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try {
      onSaved(await api.updateProduct(p.id, { changeover: items.filter((i) => i.text.trim() || i.actionId) }));
      platform.haptic('success');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Sheet title={`Переналадка на ${p.code}`} onClose={onClose}>
      <p className="small muted" style={{ margin: 0 }}>
        Что меняем и на что — по пунктам. ⓘ — «как сделать», фото, видео. «Из библиотеки» — типовые действия: одно и то же действие подключается к разным номенклатурам.
      </p>
      <div className="proc-edit">
        <ItemsEditor label="Переналадка" items={items} onChange={setItems} equipmentId={null} files={files} onFile={(f) => setFiles((l) => [...l, f])} />
      </div>
      <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={() => void save()}>
        Сохранить
      </button>
    </Sheet>
  );
}
